import {
  AXIS_GROUP_LABELS,
  AxisGroup,
  MEASURES,
  MeasureV2,
} from "./measures.ts";
import {
  ReportConfigV2,
  ReportDataSettingsV2,
} from "./report-configuration.ts";
import { DIMENSIONS, findTimeDimensionV2 } from "./dimensions.ts";
import { applyFieldNameOverride } from "./user.ts";

const getYAxisLabel = (
  axisGroup: AxisGroup,
  axisGroupMeasure: MeasureV2 | undefined,
  overrideLabel: string | undefined,
  showCount: boolean,
): string => {
  if (overrideLabel) {
    return overrideLabel;
  }

  if (axisGroupMeasure?.axisLabelOverrideCount && showCount) {
    return axisGroupMeasure.axisLabelOverrideCount;
  }

  if (axisGroupMeasure?.axisLabelOverridePercent && !showCount) {
    return axisGroupMeasure.axisLabelOverridePercent;
  }

  // Special cases
  if (axisGroupMeasure?.measure === "TERMINATIONS" && !showCount) {
    return "Attrition Rate (%)";
  }

  let label = AXIS_GROUP_LABELS[axisGroup] || "";
  if (!showCount) {
    label += " (%)";
  }
  return label;
};

export const generateReportTitle = (
  config: ReportDataSettingsV2,
  fieldNameOverrides?: Partial<Record<string, string>>,
): string => {
  if (!config.measures.length) return "New Report";

  const measurePart = config.measures
    .map((m) => MEASURES[m].label)
    .join(" and ");
  const nonMeasureDimensions = config.dimensions.filter(
    (d) => !config.measures.includes(d as keyof typeof MEASURES),
  );

  if (nonMeasureDimensions.length === 0) {
    return measurePart;
  }

  const dimensionsPart = nonMeasureDimensions
    .map((d) =>
      applyFieldNameOverride(DIMENSIONS[d].label, d, fieldNameOverrides),
    )
    .join(" by ");
  return `${measurePart} by ${dimensionsPart}`;
};

export const getAxisLabels = (
  config: ReportConfigV2,
  fieldNameOverrides?: Partial<Record<string, string>>,
) => {
  let axis1Label = "";
  let axis2Label = "";
  const measures = config.data.measures.map((m) => MEASURES[m]);
  const axisGroups = Array.from(
    new Set<string>(measures.map((m) => m.axisGroup)),
  ) as AxisGroup[];
  const firstAxisGroupMeasure = measures.find(
    (m) => m.axisGroup === axisGroups[0],
  );
  const secondAxisGroupMeasure = measures.find(
    (m) => m.axisGroup === axisGroups[1],
  );

  axis1Label = getYAxisLabel(
    axisGroups[0],
    firstAxisGroupMeasure,
    config.chart.yAxisLabelOverride1,
    config.chart.showCount,
  );

  if (axisGroups.length > 1) {
    axis2Label = getYAxisLabel(
      axisGroups[1],
      secondAxisGroupMeasure,
      config.chart.yAxisLabelOverride2,
      config.chart.showCount,
    );
  }

  let xAxisLabel = "";
  if (config.chart.xAxisLabelOverride) {
    xAxisLabel = config.chart.xAxisLabelOverride;
  } else {
    const timeDim = findTimeDimensionV2(config.data);
    if (timeDim) {
      xAxisLabel = applyFieldNameOverride(
        timeDim.label,
        timeDim.dimension,
        fieldNameOverrides,
      );
    } else {
      let labelComps = measures.map((m) => m.label).join(" and ");
      const dimensions = config.data.dimensions.map((d) =>
        applyFieldNameOverride(DIMENSIONS[d].label, d, fieldNameOverrides),
      );
      if (dimensions.length) {
        labelComps += ` by ${dimensions.join(" by ")}`;
      }
      xAxisLabel = labelComps;
    }
  }

  return {
    yAxisLeftLabel: axis1Label,
    yAxisRightLabel: axis2Label,
    xAxisLabel,
    byGroup: {
      [axisGroups[0]]: axis1Label,
      [axisGroups[1]]: axis2Label,
    },
  };
};
